import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Form,
    Select,
    DatePicker,
    notification,
    Popconfirm,
    Tooltip,
    Badge,
    InputNumber,
    Alert,
    Modal,
    Row,
    Col,
    Tag,
    Divider,
    Checkbox,
    Popover,
    Collapse
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    BarcodeOutlined,
    QrcodeOutlined,
    UserOutlined,
    WarningOutlined,
    ExportOutlined,
    ImportOutlined,
    FilterOutlined,
    ReloadOutlined,
    CalendarOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons';
import { inventoryService } from '../services/inventoryService';
import { itemService } from '../services/itemService';
import { vendorService } from '../services/vendorService';
import dayjs from 'dayjs';
import api from "../services/api";

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const AssetTracker = () => {
    const [assets, setAssets] = useState([]);
    const [filteredAssets, setFilteredAssets] = useState([]);
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [form] = Form.useForm();
    const [numItems, setNumItems] = useState(1);
    const [showSerialInput, setShowSerialInput] = useState(false);
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [capacityUnit, setCapacityUnit] = useState('kW');
    const [showCustomerField, setShowCustomerField] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerSearchTimer, setCustomerSearchTimer] = useState(null);
    const [customerSearchText, setCustomerSearchText] = useState('');
    
    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        status: [],
        brand: [],
        vendorId: [],
        customerId: [],
        itemType: [],
        purchaseDate: null,
        assignedDate: null,
        inStockOnly: false,
        assignedOnly: false,
        unassignedOnly: false
    });
    
    // Assignment modal states
    const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [assigning, setAssigning] = useState(false);

    const fetchAssets = async (search = '') => {
        try {
            setLoading(true);
            const response = await inventoryService.getAllAssets({ search });
            if (response.success) {
                const assetsData = response.data || [];
                setAssets(assetsData);
                setFilteredAssets(assetsData);
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch assets'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await itemService.getAllItems({});
            if (response.success) {
                setItems(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch items');
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await vendorService.getAllVendors({ limit: 1000 });
            if (response?.success && response?.data) {
                setVendors(Array.isArray(response.data) ? response.data : []);
            } else {
                setVendors([]);
            }
        } catch (error) {
            console.log('Vendors service not available:', error?.error || error?.message);
            setVendors([]);
        }
    };

    const fetchCustomers = async (search = '') => {
        try {
            setLoadingCustomers(true);
            const url = `/customers?limit=1000`;
            const response = await api.get(url);
            if (response.success && Array.isArray(response.data)) {
                setCustomers(response.data);
            } else {
                setCustomers([]);
            }
        } catch (error) {
            console.error('Network error:', error);
            setCustomers([]);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleCustomerSearch = (value) => {
        setCustomerSearchText(value || '');
    };

    // Case-insensitive filtered list for dropdowns
    const filteredCustomerOptions = customerSearchText
        ? customers.filter(c => {
            const q = customerSearchText.toLowerCase();
            return (
                (c.customerName || '').toLowerCase().includes(q) ||
                (c.customerMobile || '').toLowerCase().includes(q) ||
                (c.customerEmail || '').toLowerCase().includes(q) ||
                (c.consumerNo || '').toLowerCase().includes(q)
            );
          })
        : customers;

    // Apply filters function
    const applyFilters = () => {
        let filtered = [...assets];

        // Search filter
        if (searchText) {
            filtered = filtered.filter(item =>
                Object.values(item).some(value => {
                    if (typeof value === 'object' && value !== null) {
                        return Object.values(value).some(subValue =>
                            String(subValue).toLowerCase().includes(searchText.toLowerCase())
                        );
                    }
                    return String(value).toLowerCase().includes(searchText.toLowerCase());
                })
            );
        }

        // Status filter
        if (activeFilters.status.length > 0) {
            filtered = filtered.filter(item =>
                activeFilters.status.includes(item.status)
            );
        }

        // Brand filter
        if (activeFilters.brand.length > 0) {
            filtered = filtered.filter(item =>
                activeFilters.brand.some(brand => 
                    item.brand && item.brand.toLowerCase().includes(brand.toLowerCase())
                )
            );
        }

        // Vendor filter
        if (activeFilters.vendorId.length > 0) {
            filtered = filtered.filter(item =>
                activeFilters.vendorId.includes(item.vendorId)
            );
        }

        // Customer filter
        if (activeFilters.customerId.length > 0) {
            filtered = filtered.filter(item =>
                activeFilters.customerId.includes(item.customerId)
            );
        }

        // Item Type filter
        if (activeFilters.itemType.length > 0) {
            filtered = filtered.filter(item =>
                item.item && activeFilters.itemType.includes(item.item.itemType)
            );
        }

        // Purchase Date filter (single date)
        if (activeFilters.purchaseDate) {
            filtered = filtered.filter(item => {
                if (!item.purchaseDate) return false;
                try {
                    return dayjs(item.purchaseDate).format('YYYY-MM-DD') === activeFilters.purchaseDate.format('YYYY-MM-DD');
                } catch (error) {
                    return false;
                }
            });
        }

        // Assigned Date filter (single date)
        if (activeFilters.assignedDate) {
            filtered = filtered.filter(item => {
                if (!item.assignedDate) return false;
                try {
                    return dayjs(item.assignedDate).format('YYYY-MM-DD') === activeFilters.assignedDate.format('YYYY-MM-DD');
                } catch (error) {
                    return false;
                }
            });
        }

        // In Stock only filter
        if (activeFilters.inStockOnly) {
            filtered = filtered.filter(item => item.status === 'InStock');
        }

        // Assigned only filter
        if (activeFilters.assignedOnly) {
            filtered = filtered.filter(item => item.status === 'Assigned');
        }

        // Unassigned only filter
        if (activeFilters.unassignedOnly) {
            filtered = filtered.filter(item => item.status !== 'Assigned');
        }

        setFilteredAssets(filtered);
    };

    // Reset filters
    const resetFilters = () => {
        setSearchText('');
        setActiveFilters({
            status: [],
            brand: [],
            vendorId: [],
            customerId: [],
            itemType: [],
            purchaseDate: null,
            assignedDate: null,
            inStockOnly: false,
            assignedOnly: false,
            unassignedOnly: false
        });
        setFilteredAssets(assets);
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setActiveFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Clear specific filter
    const clearFilter = (filterName) => {
        const defaultValue = Array.isArray(activeFilters[filterName]) ? [] : 
                           filterName.includes('Date') || filterName.includes('capacity') ? null : false;
        handleFilterChange(filterName, defaultValue);
    };

    // Count active filters
    const activeFilterCount = Object.entries(activeFilters).reduce((count, [key, value]) => {
        if (Array.isArray(value)) {
            return count + value.length;
        } else if (value === true) {
            return count + 1;
        } else if (value && key.includes('Date')) {
            return count + 1;
        }
        return count;
    }, 0);

    // Apply filters whenever activeFilters or searchText changes
    useEffect(() => {
        applyFilters();
    }, [activeFilters, searchText, assets]);

    // Fetch data on component mount
    useEffect(() => {
        fetchAssets();
        fetchItems();
        fetchVendors();
        fetchCustomers();
    }, []);

    // Extract unique values for filters
    const statusOptions = [...new Set(assets.map(item => item.status).filter(Boolean))];
    const brandOptions = [...new Set(assets.map(item => item.brand).filter(Boolean))];
    const vendorOptions = vendors;
    const customerOptions = customers;
    const itemTypeOptions = [...new Set(items.map(item => item.itemType).filter(Boolean))];

    const assignAssetToCustomer = async (serialNumber, customerId) => {
        try {
            setAssigning(true);
            const response = await api.post('/asset-assignment/assign', { serialNumber, customerId });
            const data = response;
            
            if (data.success) {
                notification.success({
                    message: 'Success',
                    description: 'Asset assigned successfully!'
                });
                
                fetchAssets(searchText);
                fetchItems();
                
                return true;
            } else {
                throw new Error(data.error || 'Assignment failed');
            }
            
        } catch (error) {
            console.error('Assign error:', error);
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to assign asset'
            });
            return false;
        } finally {
            setAssigning(false);
        }
    };

    const unassignAsset = async (serialNumber) => {
        try {
            setAssigning(true);
            const response = await api.post('/asset-assignment/unassign', { serialNumber });
            const data = response;
            
            if (data.success) {
                notification.success({
                    message: 'Success',
                    description: 'Asset unassigned successfully!'
                });
                
                fetchAssets(searchText);
                fetchItems();
                
                return true;
            } else {
                throw new Error(data.error || 'Unassignment failed');
            }
            
        } catch (error) {
            console.error('Unassign error:', error);
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to unassign asset'
            });
            return false;
        } finally {
            setAssigning(false);
        }
    };

    const handleStatusChange = (value) => {
        setShowCustomerField(value === 'Assigned');
        if (value !== 'Assigned') {
            form.setFieldsValue({ customerId: null });
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchAssets(value);
    };

    const handleAdd = () => {
        setEditingAsset(null);
        form.resetFields();
        setNumItems(1);
        setShowSerialInput(false);
        setIsBatchMode(false);
        setShowCustomerField(false);
        setCapacityUnit('kW');
        setIsDrawerVisible(true);
    };

    const calcWarrantyExpiry = (itemId, baseDate) => {
        const selectedItem = items.find(i => i.id === itemId);
        if (selectedItem && selectedItem.warrantyMonths > 0) {
            const base = baseDate || dayjs();
            return base.add(selectedItem.warrantyMonths, 'year');
        }
        return null;
    };

    const handleItemChange = (itemId) => {
        const selectedItem = items.find(i => i.id === itemId);
        if (selectedItem) {
            const unit = selectedItem.itemType === 'SolarPanel' ? 'Wp' : 'kW';
            setCapacityUnit(unit);
            form.setFieldsValue({ capacityUnit: unit });
        }
        if (!editingAsset) {
            const purchaseDate = form.getFieldValue('purchaseDate');
            const expiry = calcWarrantyExpiry(itemId, purchaseDate);
            if (expiry) form.setFieldsValue({ warrantyExpiry: expiry });
        }
    };

    const handlePurchaseDateChange = (date) => {
        const itemId = form.getFieldValue('itemId');
        if (itemId && date) {
            const expiry = calcWarrantyExpiry(itemId, date);
            if (expiry) form.setFieldsValue({ warrantyExpiry: expiry });
        }
    };

    const handleEdit = (record) => {
        setEditingAsset(record);
        setNumItems(1);
        setShowSerialInput(true);
        setIsBatchMode(false);

        // Set capacity unit based on item type
        const itemType = record.item?.itemType;
        const unit = record.capacityUnit || (itemType === 'SolarPanel' ? 'Wp' : 'kW');
        setCapacityUnit(unit);

        const isAssigned = record.status === 'Assigned';
        setShowCustomerField(isAssigned);

        form.setFieldsValue({
            ...record,
            purchaseDate: record.purchaseDate ? dayjs(record.purchaseDate) : null,
            warrantyExpiry: record.warrantyExpiry ? dayjs(record.warrantyExpiry) : null,
            customerId: record.customerId || null,
            capacityUnit: unit
        });
        setIsDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await inventoryService.deleteAsset(id);
            notification.success({
                message: 'Success',
                description: 'Asset deleted successfully'
            });
            fetchAssets(searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Failed to delete asset'
            });
        }
    };

    const handleAssignClick = (record) => {
        setSelectedAsset(record);
        setSelectedCustomerId('');
        setIsAssignmentModalVisible(true);
    };

    const handleUnassignClick = async (record) => {
        Modal.confirm({
            title: 'Unassign Asset',
            icon: <WarningOutlined style={{ color: '#fa8c16' }} />,
            content: (
                <div>
                    <p>Are you sure you want to unassign this asset?</p>
                    <Alert
                        type="warning"
                        message="Asset will return to inventory"
                        description="The asset will be available for re-assignment and inventory count will increase by 1."
                        className="mt-3"
                    />
                </div>
            ),
            okText: 'Yes, Unassign',
            cancelText: 'Cancel',
            okButtonProps: { 
                type: 'primary',
                danger: true 
            },
            async onOk() {
                const success = await unassignAsset(record.serialNumber);
                if (success) {
                    setIsAssignmentModalVisible(false);
                }
            }
        });
    };

    const handleAssignmentConfirm = async () => {
        if (!selectedCustomerId) {
            notification.warning({
                message: 'Warning',
                description: 'Please select a customer'
            });
            return;
        }

        const customer = customers.find(c => c.id === selectedCustomerId);
        const customerName = customer ? customer.customerName : 'Customer';

        Modal.confirm({
            title: 'Permanent Assignment',
            icon: <WarningOutlined style={{ color: '#fa8c16' }} />,
            content: (
                <div>
                    <p>Asset <strong>{selectedAsset?.serialNumber}</strong> will be assigned to:</p>
                    <p className="text-lg font-bold my-2">{customerName}</p>
                    <Alert
                        type="warning"
                        message="This action cannot be reversed!"
                        description="The asset will be permanently removed from available inventory."
                        className="mt-3"
                    />
                </div>
            ),
            okText: 'Confirm Assignment',
            cancelText: 'Cancel',
            okButtonProps: { 
                type: 'primary',
                danger: true 
            },
            async onOk() {
                const success = await assignAssetToCustomer(
                    selectedAsset.serialNumber,
                    selectedCustomerId
                );
                
                if (success) {
                    setIsAssignmentModalVisible(false);
                    setSelectedAsset(null);
                    setSelectedCustomerId('');
                }
            }
        });
    };

    const handleNumItemsChange = (value) => {
        setNumItems(value);
        if (value > 1) {
            setIsBatchMode(true);
            setShowSerialInput(true);
        } else {
            setIsBatchMode(false);
            setShowSerialInput(true);
        }
    };

    const parseSerialNumbers = (serialInput) => {
        if (!serialInput) return [];
        
        const serials = serialInput
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        
        return serials;
    };

    const showAssignmentWarning = (assetData, customerName) => {
        return new Promise((resolve) => {
            Modal.confirm({
                title: 'Permanent Assignment',
                icon: <WarningOutlined style={{ color: '#fa8c16' }} />,
                content: (
                    <div>
                        <p>Asset <strong>{assetData.serialNumber}</strong> will be assigned to:</p>
                        <p className="text-lg font-bold my-2">{customerName}</p>
                        <Alert
                            type="warning"
                            message="This action cannot be reversed!"
                            description="The asset will be permanently removed from available inventory."
                            className="mt-3"
                        />
                    </div>
                ),
                okText: 'Confirm Assignment',
                cancelText: 'Cancel',
                okButtonProps: { 
                    type: 'primary',
                    danger: true 
                },
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });
    };

    const onFinish = async (values) => {
        try {
            if (isBatchMode && editingAsset) {
                notification.error({
                    message: 'Error',
                    description: 'Batch mode is only available for creating new assets, not editing'
                });
                return;
            }

            const isAssigning = values.status === 'Assigned' && values.customerId;
            // Only show warning if this is a NEW assignment (not just editing an already-assigned asset)
            const wasAssigned = editingAsset && editingAsset.status === 'Assigned' && editingAsset.customerId;
            const isSameAssignment = wasAssigned && isAssigning && values.customerId === editingAsset.customerId;
            const isNewAssignment = isAssigning && !isSameAssignment;

            let customerName = '';
            if (isNewAssignment) {
                const customer = customers.find(c => c.id === values.customerId);
                customerName = customer ? (customer.name || customer.customerName) : 'Customer';

                const confirmed = await showAssignmentWarning(
                    editingAsset || { serialNumber: 'New Asset' },
                    customerName
                );

                if (!confirmed) {
                    return;
                }
            } else if (isAssigning) {
                const customer = customers.find(c => c.id === values.customerId);
                customerName = customer ? (customer.name || customer.customerName) : 'Customer';
            }

            if (isBatchMode) {
                const serialNumbers = parseSerialNumbers(values.serialNumber);
                
                if (serialNumbers.length !== numItems) {
                    notification.error({
                        message: 'Error',
                        description: `Please enter exactly ${numItems} serial numbers separated by commas`
                    });
                    return;
                }

                const uniqueSerials = new Set(serialNumbers);
                if (uniqueSerials.size !== serialNumbers.length) {
                    notification.error({
                        message: 'Error',
                        description: 'Duplicate serial numbers are not allowed'
                    });
                    return;
                }

                const createPromises = serialNumbers.map(serialNumber => {
                    const payload = {
                        ...values,
                        serialNumber: serialNumber,
                        capacity: values.capacity ? parseFloat(values.capacity) : null,
                        capacityUnit: values.capacityUnit || 'kW',
                        purchaseDate: values.purchaseDate ? values.purchaseDate.format('YYYY-MM-DD') : null,
                        warrantyExpiry: values.warrantyExpiry ? values.warrantyExpiry.format('YYYY-MM-DD') : null,
                        customerId: isAssigning ? values.customerId : null,
                        assignedDate: isAssigning ? new Date().toISOString() : null
                    };
                    return inventoryService.createAsset(payload);
                });

                await Promise.all(createPromises);
                
                notification.success({
                    message: 'Success',
                    description: `Created ${serialNumbers.length} assets${isAssigning ? ` and assigned to ${customerName}` : ''}`
                });
            } else {
                const payload = {
                    ...values,
                    capacity: values.capacity ? parseFloat(values.capacity) : null,
                    capacityUnit: values.capacityUnit || 'kW',
                    purchaseDate: values.purchaseDate ? values.purchaseDate.format('YYYY-MM-DD') : null,
                    warrantyExpiry: values.warrantyExpiry ? values.warrantyExpiry.format('YYYY-MM-DD') : null,
                    customerId: isAssigning ? values.customerId : null,
                    assignedDate: isNewAssignment ? new Date().toISOString()
                        : (editingAsset ? editingAsset.assignedDate || null : null)
                };

                if (editingAsset) {
                    await inventoryService.updateAsset(editingAsset.id, payload);

                    let message = 'Asset updated successfully';
                    if (isNewAssignment) {
                        message += ` and assigned to ${customerName}`;
                    } else if (isSameAssignment) {
                        message += ` (assigned to ${customerName})`;
                    } else if (wasAssigned && values.status !== 'Assigned') {
                        message += ' (assignment removed)';
                    }
                    
                    notification.success({
                        message: 'Success',
                        description: message
                    });
                } else {
                    await inventoryService.createAsset(payload);
                    
                    let message = 'Asset created successfully';
                    if (isAssigning) {
                        message += ` and assigned to ${customerName}`;
                    }
                    
                    notification.success({
                        message: 'Success',
                        description: message
                    });
                }
            }
            
            setIsDrawerVisible(false);
            setShowCustomerField(false);
            fetchAssets(searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Operation failed'
            });
        }
    };

    // Compact filter content
    const filterContent = (
        <div style={{ width: 300 }}>
            <Form layout="vertical" size="small">
                <div className="mb-2">
                    <strong className="text-sm">Quick Filters</strong>
                </div>
                <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
                    <Checkbox
                        checked={activeFilters.inStockOnly}
                        onChange={(e) => handleFilterChange('inStockOnly', e.target.checked)}
                        style={{ fontSize: '12px' }}
                    >
                        In Stock Only
                    </Checkbox>
                    <Checkbox
                        checked={activeFilters.assignedOnly}
                        onChange={(e) => handleFilterChange('assignedOnly', e.target.checked)}
                        style={{ fontSize: '12px' }}
                    >
                        Assigned Only
                    </Checkbox>
                </Space>

                <Divider style={{ margin: '8px 0' }} />

                <Collapse ghost size="small">
                    <Panel header="Status" key="1">
                        <Checkbox.Group
                            value={activeFilters.status}
                            onChange={(value) => handleFilterChange('status', value)}
                            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                        >
                            {statusOptions.map(status => (
                                <Checkbox key={status} value={status} style={{ fontSize: '12px' }}>
                                    {status === 'InStock' ? 'In Stock' : status === 'Assigned' ? 'Assigned' : status}
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Panel>
                    
                    <Panel header="Brand & Vendor" key="2">
                        <div className="space-y-3">
                            <Form.Item label="Brand" style={{ marginBottom: 8 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select brands"
                                    value={activeFilters.brand}
                                    onChange={(value) => handleFilterChange('brand', value)}
                                    size="small"
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {brandOptions.map(brand => (
                                        <Option key={brand} value={brand}>{brand}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            
                            <Form.Item label="Vendor" style={{ marginBottom: 8 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select vendors"
                                    value={activeFilters.vendorId}
                                    onChange={(value) => handleFilterChange('vendorId', value)}
                                    size="small"
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {vendorOptions.map(vendor => (
                                        <Option key={vendor.id} value={vendor.id}>{vendor.vendorName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </Panel>
                    
                    <Panel header="Customer & Item Type" key="3">
                        <div className="space-y-3">
                            <Form.Item label="Customer" style={{ marginBottom: 8 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Search & select customers"
                                    value={activeFilters.customerId}
                                    onChange={(value) => handleFilterChange('customerId', value)}
                                    size="small"
                                    allowClear
                                    showSearch
                                    onSearch={handleCustomerSearch}
                                    filterOption={false}
                                    loading={loadingCustomers}
                                    style={{ width: '100%' }}
                                >
                                    {filteredCustomerOptions.map(customer => (
                                        <Option key={customer.id} value={customer.id}>
                                            {customer.customerName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            
                            <Form.Item label="Item Type" style={{ marginBottom: 8 }}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select item types"
                                    value={activeFilters.itemType}
                                    onChange={(value) => handleFilterChange('itemType', value)}
                                    size="small"
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {itemTypeOptions.map(type => (
                                        <Option key={type} value={type}>{type}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </Panel>
                    
                    <Panel header="Date Filters" key="5">
                        <div className="space-y-3">
                            <Form.Item label="Purchase Date" style={{ marginBottom: 8 }}>
                                <DatePicker
                                    value={activeFilters.purchaseDate}
                                    onChange={(date) => handleFilterChange('purchaseDate', date)}
                                    onOpenChange={(open) => setDatePickerOpen(open)}
                                    size="small"
                                    style={{ width: '100%' }}
                                    allowClear
                                    placeholder="Select date"
                                    format="MMM D, YYYY"
                                />
                            </Form.Item>

                            <Form.Item label="Assigned Date" style={{ marginBottom: 8 }}>
                                <DatePicker
                                    value={activeFilters.assignedDate}
                                    onChange={(date) => handleFilterChange('assignedDate', date)}
                                    onOpenChange={(open) => setDatePickerOpen(open)}
                                    size="small"
                                    style={{ width: '100%' }}
                                    allowClear
                                    placeholder="Select date"
                                    format="MMM D, YYYY"
                                />
                            </Form.Item>
                        </div>
                    </Panel>
                </Collapse>

                <Divider style={{ margin: '12px 0' }} />

                <div className="flex justify-between">
                    <Button
                        type="link"
                        size="small"
                        onClick={resetFilters}
                        style={{ padding: 0, fontSize: '12px' }}
                    >
                        Clear All
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => setShowFilters(false)}
                    >
                        Apply
                    </Button>
                </div>
            </Form>
        </div>
    );

    const columns = [
        {
            title: 'Serial Number',
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            render: (text) => (
                <Space>
                    <BarcodeOutlined style={{ color: '#595959' }} />
                    <span className="font-mono font-medium">{text}</span>
                </Space>
            )
        },
        {
            title: 'Item',
            dataIndex: ['item', 'itemName'],
            key: 'itemName',
        },
        {
            title: 'Capacity',
            dataIndex: 'capacity',
            key: 'capacity',
            width: 120,
            render: (capacity, record) => {
                if (!capacity) return <span className="text-gray-400">-</span>;
                
                const capacityValue = parseFloat(capacity);
                const capacityUnit = record.capacityUnit || (record.item?.itemType === 'SolarPanel' ? 'Wp' : 'kW');
                
                return (
                    <div className="font-medium text-blue-600">
                        {capacityValue.toFixed(capacityValue % 1 === 0 ? 0 : 1)}
                        <span className="text-xs text-gray-500 ml-1">{capacityUnit}</span>
                    </div>
                );
            },
            sorter: (a, b) => (a.capacity || 0) - (b.capacity || 0)
        },
        {
            title: 'Brand / Vendor',
            key: 'source',
            render: (text, record) => (
                <div className="text-xs text-gray-500">
                    {record.brand && <div>Brand: {record.brand}</div>}
                    {record.vendor?.vendorName && (
                        <div>Vendor: <span className="font-medium">{record.vendor.vendorName}</span></div>
                    )}
                    {!record.vendor?.vendorName && record.dealer && (
                        <div>Dealer: {record.dealer}</div>
                    )}
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'In Stock', value: 'InStock' },
                { text: 'Assigned', value: 'Assigned' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status) => (
                <Badge 
                    status={status === 'InStock' ? 'success' : status === 'Assigned' ? 'processing' : 'error'} 
                    text={status === 'InStock' ? 'In Stock' : status === 'Assigned' ? 'Assigned' : status}
                />
            )
        },
        {
            title: 'Assigned To',
            dataIndex: 'customer',
            key: 'customer',
            render: (customer, record) => {
                if (record.status === 'Assigned' && customer) {
                    return (
                        <div className="flex items-center gap-2">
                            <UserOutlined className="text-blue-500" />
                            <div>
                                <div className="font-medium">{customer.customerName}</div>
                                {record.assignedDate && (
                                    <div className="text-xs text-gray-400">
                                        {new Date(record.assignedDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                }
                return <span className="text-gray-300">-</span>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (text, record) => (
                <Space>
                    {record.status === 'InStock' && (
                        <Tooltip title="Assign to Customer">
                            <Button
                                type="text"
                                size="small"
                                icon={<ExportOutlined style={{ color: '#1890ff' }} />}
                                onClick={() => handleAssignClick(record)}
                            />
                        </Tooltip>
                    )}
                    
                    {record.status === 'Assigned' && (
                        <Tooltip title="Unassign from Customer">
                            <Button
                                type="text"
                                size="small"
                                icon={<ImportOutlined style={{ color: '#ff4d4f' }} />}
                                onClick={() => handleUnassignClick(record)}
                            />
                        </Tooltip>
                    )}
                    
                    <Tooltip title="Edit Asset Details">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ color: '#fa8c16' }} />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    
                    {record.status !== 'Assigned' && (
                        <Popconfirm
                            title="Delete Asset"
                            description="Are you sure? Assigned assets cannot be deleted."
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="p-6">
            <Card
                title={
                    <div className="flex items-center gap-2">
                        <QrcodeOutlined />
                        <span>Asset Tracker (Serialized)</span>
                        {activeFilterCount > 0 && (
                            <Badge count={activeFilterCount} size="small" style={{ marginLeft: 8 }} />
                        )}
                    </div>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Add New Asset
                    </Button>
                }
            >
                {/* Search and Filter Bar */}
                <div className="mb-4">
                    <Row gutter={[12, 12]} align="middle">
                        <Col flex="auto">
                            <Input.Search
                                placeholder="Search by serial number, item, brand, vendor..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onSearch={handleSearch}
                                style={{ width: '100%' }}
                                allowClear
                                size="middle"
                            />
                        </Col>
                        <Col>
                            <Popover
                                title={
                                    <div className="flex justify-between items-center">
                                        <span>Filters</span>
                                        {activeFilterCount > 0 && (
                                            <Button 
                                                type="link" 
                                                size="small" 
                                                onClick={resetFilters}
                                                style={{ padding: 0, fontSize: '12px' }}
                                            >
                                                Clear All
                                            </Button>
                                        )}
                                    </div>
                                }
                                content={filterContent}
                                trigger="click"
                                open={showFilters}
                                onOpenChange={(visible) => {
                                    if (!visible && datePickerOpen) return;
                                    setShowFilters(visible);
                                }}
                                placement="bottomRight"
                            >
                                <Button 
                                    icon={<FilterOutlined />} 
                                    type={activeFilterCount > 0 ? "primary" : "default"}
                                >
                                    Filters
                                </Button>
                            </Popover>
                        </Col>
                        <Col>
                            <Tooltip title="Refresh">
                                <Button 
                                    icon={<ReloadOutlined />} 
                                    onClick={() => {
                                        resetFilters();
                                        fetchAssets();
                                    }}
                                    loading={loading}
                                />
                            </Tooltip>
                        </Col>
                    </Row>

                    {/* Active Filters Display  */}
                    {activeFilterCount > 0 && (
                        <div className="mt-3">
                            <div className="text-xs text-gray-500 mb-1">Active filters:</div>
                            <Space size={[4, 4]} wrap>
                                {activeFilters.status.map(status => (
                                    <Tag
                                        key={status}
                                        closable
                                        onClose={() => handleFilterChange('status', activeFilters.status.filter(s => s !== status))}
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        {status === 'InStock' ? 'In Stock' : status}
                                    </Tag>
                                ))}
                                {activeFilters.brand.map(brand => (
                                    <Tag
                                        key={brand}
                                        closable
                                        onClose={() => handleFilterChange('brand', activeFilters.brand.filter(b => b !== brand))}
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        Brand: {brand}
                                    </Tag>
                                ))}
                                {activeFilters.vendorId.map(vendorId => {
                                    const vendor = vendors.find(v => v.id === vendorId);
                                    return vendor ? (
                                        <Tag
                                            key={vendorId}
                                            closable
                                            onClose={() => handleFilterChange('vendorId', activeFilters.vendorId.filter(v => v !== vendorId))}
                                            size="small"
                                            style={{ fontSize: '11px', padding: '0 6px' }}
                                        >
                                            Vendor: {vendor.vendorName}
                                        </Tag>
                                    ) : null;
                                })}
                                {activeFilters.customerId.map(customerId => {
                                    const customer = customers.find(c => c.id === customerId);
                                    return customer ? (
                                        <Tag
                                            key={customerId}
                                            closable
                                            onClose={() => handleFilterChange('customerId', activeFilters.customerId.filter(c => c !== customerId))}
                                            size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                        >
                                            Cust: {customer.customerName.length > 12 ? customer.customerName.substring(0, 12) + '...' : customer.customerName}
                                        </Tag>
                                    ) : null;
                                })}
                                {activeFilters.itemType.map(type => (
                                    <Tag
                                        key={type}
                                        closable
                                        onClose={() => handleFilterChange('itemType', activeFilters.itemType.filter(t => t !== type))}
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        Type: {type}
                                    </Tag>
                                ))}
                                {activeFilters.purchaseDate && (
                                    <Tag
                                        closable
                                        onClose={() => clearFilter('purchaseDate')}
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        Purchase: {activeFilters.purchaseDate.format('MMM D')}
                                    </Tag>
                                )}
                                {activeFilters.assignedDate && (
                                    <Tag
                                        closable
                                        onClose={() => clearFilter('assignedDate')}
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        Assigned: {activeFilters.assignedDate.format('MMM D')}
                                    </Tag>
                                )}
                                {activeFilters.inStockOnly && (
                                    <Tag
                                        closable
                                        onClose={() => clearFilter('inStockOnly')}
                                        color="green"
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        In Stock
                                    </Tag>
                                )}
                                {activeFilters.assignedOnly && (
                                    <Tag
                                        closable
                                        onClose={() => clearFilter('assignedOnly')}
                                        color="blue"
                                        size="small"
                                        style={{ fontSize: '11px', padding: '0 6px' }}
                                    >
                                        Assigned
                                    </Tag>
                                )}
                            </Space>
                        </div>
                    )}

                    {/* Results Count */}
                    <div className="mt-2 text-xs text-gray-500">
                        Showing {filteredAssets.length} of {assets.length} assets
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredAssets}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    size="middle"
                />
            </Card>

            {/* Edit/Create Asset Drawer */}
            <Drawer
                title={editingAsset ? "Edit Asset" : "New Asset"}
                width={500}
                onClose={() => {
                    setIsDrawerVisible(false);
                    setShowCustomerField(false);
                }}
                open={isDrawerVisible}
                maskClosable={false}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="itemId"
                        label="Product / Item"
                        rules={[{ required: true, message: 'Please select an item' }]}
                    >
                        <Select showSearch optionFilterProp="children" placeholder="Select item type" onChange={handleItemChange}>
                            {items.map(item => (
                                <Option key={item.id} value={item.id}>
                                    {item.itemName} <span className="text-gray-400 text-xs">({item.itemType})</span>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {!editingAsset && (
                        <>
                            <Form.Item
                                label="Number of Items"
                                rules={[{ required: true, message: 'Please enter number of items' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={100}
                                    value={numItems}
                                    onChange={handleNumItemsChange}
                                    className="w-full"
                                    placeholder="Enter number of items"
                                />
                            </Form.Item>

                            {isBatchMode && (
                                <Alert
                                    message={`Batch Mode: Enter ${numItems} serial numbers`}
                                    description="Separate each serial number with a comma (e.g., SN001,SN002,SN003)"
                                    type="info"
                                    showIcon
                                    className="mb-4"
                                />
                            )}
                        </>
                    )}

                    <Form.Item
                        name="serialNumber"
                        label={isBatchMode ? `Serial Numbers (${numItems} required)` : "Serial Number"}
                        rules={[{ required: true, message: isBatchMode ? `Please enter ${numItems} serial numbers` : 'Please enter serial number' }]}
                    >
                        {isBatchMode ? (
                            <TextArea
                                rows={4}
                                placeholder={`Enter ${numItems} serial numbers separated by commas
Example: SN001,SN002,SN003`}
                            />
                        ) : (
                            <Input placeholder="Enter serial number" />
                        )}
                    </Form.Item>

                    <Form.Item name="capacity" label={`Capacity (${capacityUnit})`}>
                        <InputNumber
                            min={0}
                            step={0.1}
                            placeholder={capacityUnit === 'Wp' ? 'e.g., 540 Wp' : 'e.g., 10.5 kW'}
                            className="w-full"
                            formatter={value => value ? `${value}` : ''}
                            parser={value => value ? parseFloat(value.replace(/[^\d.]/g, '')) : null}
                            addonAfter={<span style={{ padding: '0 8px', fontWeight: 600 }}>{capacityUnit}</span>}
                        />
                    </Form.Item>

                    <Form.Item
                        name="capacityUnit"
                        hidden
                        initialValue="kW"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item name="brand" label="Brand">
                        <Input placeholder="e.g. Waaree, Rishen" />
                    </Form.Item>

                    <Form.Item name="vendorId" label="Vendor">
                        <Select 
                            showSearch 
                            optionFilterProp="children" 
                            placeholder="Select vendor (optional)"
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {vendors.map(vendor => (
                                <Option key={vendor.id} value={vendor.id}>
                                    {vendor.vendorName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="dealer" label="Dealer (Legacy - use Vendor field above)">
                        <Input placeholder="Legacy field - use Vendor dropdown instead" />
                    </Form.Item>

                    <Form.Item name="batchId" label="Batch ID">
                        <Input placeholder="Optional" />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="purchaseDate" label="Purchase Date">
                            <DatePicker className="w-full" onChange={handlePurchaseDateChange} />
                        </Form.Item>
                        <Form.Item name="warrantyExpiry" label="Warranty Expiry (Auto)">
                            <DatePicker className="w-full" />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="status"
                        label="Status"
                        initialValue="InStock"
                    >
                        <Select onChange={handleStatusChange}>
                            <Option value="InStock">In Stock</Option>
                            <Option value="Assigned">Assigned (Manage via Orders)</Option>
                        </Select>
                    </Form.Item>

                    {showCustomerField && (
                        <>
                            <Alert
                                type="info"
                                message="Customer Assignment"
                                description="Select a customer to assign this asset. This will permanently remove it from available inventory."
                                className="mb-4"
                                showIcon
                            />
                            <Form.Item
                                name="customerId"
                                label="Select Customer"
                                rules={[
                                    { 
                                        required: showCustomerField, 
                                        message: 'Please select a customer' 
                                    }
                                ]}
                            >
                                <Select
                                    placeholder="Type to search customers..."
                                    showSearch
                                    filterOption={false}
                                    onSearch={handleCustomerSearch}
                                    loading={loadingCustomers}
                                    allowClear
                                    suffixIcon={<UserOutlined />}
                                    notFoundContent={
                                        loadingCustomers ?
                                            <div className="py-2">Searching...</div> :
                                            <div className="py-2">Type a name, mobile or consumer number</div>
                                    }
                                >
                                    {filteredCustomerOptions.map(customer => (
                                        <Option
                                            key={customer.id}
                                            value={customer.id}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">
                                                    {customer.customerName}
                                                </span>
                                                <div className="flex gap-2 text-xs text-gray-500">
                                                    {customer.customerMobile && (
                                                        <span> {customer.customerMobile}</span>
                                                    )}
                                                    {customer.customerEmail && (
                                                        <span> {customer.customerEmail}</span>
                                                    )}
                                                </div>
                                                {customer.consumerNo && (
                                                    <span className="text-xs text-gray-400">
                                                        Consumer #: {customer.consumerNo}
                                                    </span>
                                                )}
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>
                    )}

                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={() => {
                            setIsDrawerVisible(false);
                            setShowCustomerField(false);
                        }}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingAsset ? 'Update' : isBatchMode ? `Create ${numItems} Assets` : 'Create'}
                        </Button>
                    </div>
                </Form>
            </Drawer>

            {/* Assignment Modal */}
            <Modal
                title="Assign Asset to Customer"
                open={isAssignmentModalVisible}
                onCancel={() => setIsAssignmentModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsAssignmentModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button 
                        key="assign" 
                        type="primary" 
                        danger 
                        loading={assigning}
                        onClick={handleAssignmentConfirm}
                        disabled={!selectedCustomerId}
                    >
                        Assign Asset
                    </Button>
                ]}
            >
                <div className="space-y-4">
                    <Alert
                        type="info"
                        message="Asset Details"
                        description={
                            <div>
                                <p><strong>Serial Number:</strong> {selectedAsset?.serialNumber}</p>
                                <p><strong>Item:</strong> {selectedAsset?.item?.itemName}</p>
                                {selectedAsset?.capacity && (
                                    <p><strong>Capacity:</strong> {selectedAsset.capacity} {selectedAsset.capacityUnit || 'kW'}</p>
                                )}
                            </div>
                        }
                        showIcon
                    />
                    
                    <Alert
                        type="warning"
                        message="Important Note"
                        description="Assigning this asset will permanently remove it from available inventory. Stock count will be reduced by 1."
                        showIcon
                    />
                    
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Select Customer
                        </label>
                        <Select
                            placeholder="Type to search customers..."
                            style={{ width: '100%' }}
                            showSearch
                            filterOption={false}
                            onSearch={handleCustomerSearch}
                            value={selectedCustomerId}
                            onChange={(val) => { setSelectedCustomerId(val); setCustomerSearchText(''); }}
                            onBlur={() => setCustomerSearchText('')}
                            loading={loadingCustomers}
                            notFoundContent={
                                loadingCustomers ?
                                    <div className="py-2">Searching...</div> :
                                    <div className="py-2">Type a name, mobile or consumer number</div>
                            }
                        >
                            {filteredCustomerOptions.map(customer => (
                                <Option
                                    key={customer.id}
                                    value={customer.id}
                                >
                                    <div className="flex flex-col">
                                        <span className="font-medium">{customer.customerName}</span>
                                        <div className="text-xs text-gray-500">
                                            {customer.customerMobile && <span>{customer.customerMobile}</span>}
                                            {customer.customerEmail && <span>  {customer.customerEmail}</span>}
                                        </div>
                                        {customer.consumerNo && (
                                            <span className="text-xs text-gray-400">Consumer #: {customer.consumerNo}</span>
                                        )}
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AssetTracker;